import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { api, ApiError, type ApiUser } from './api';

/**
 * Result of a login attempt:
 *   * `'ok'` — session established, `token` + `user` already set on the store.
 *   * `'totp_required'` — password was correct but the account has 2FA enabled and the caller
 *     didn't supply a code. The UI moves to the TOTP-prompt step and re-calls `login` with the
 *     code filled in. NO token is set yet.
 *   * `'totp_wrong'` — caller supplied a code but it didn't match. Show an error, keep the form
 *     state, let the user retry.
 *   * `'invalid_credentials'` — wrong email/password.
 *   * `'rate_limited'` — bucket exhausted; `retryAfterSeconds` carries the wait.
 *   * `'network_error'` — anything else (mostly fetch failures / 5xx).
 */
export type LoginResult =
  | { status: 'ok' }
  | { status: 'totp_required' }
  | { status: 'totp_wrong' }
  | { status: 'invalid_credentials' }
  | { status: 'rate_limited'; retryAfterSeconds?: number }
  | { status: 'network_error' };

interface AuthState {
  token: string | null;
  user: ApiUser | null;
  /** Has the persisted state been re-validated against the server since this tab loaded? */
  hydrated: boolean;
  /**
   * Attempt to sign in. On `totp_required` the store stays in an unauthenticated state — the
   * UI's job is to flip to the second-factor input and re-invoke `login` with `totp_code` or
   * `recovery_code` populated.
   */
  login: (
    email: string,
    password: string,
    second?: { totp_code?: string; recovery_code?: string },
  ) => Promise<LoginResult>;
  signup: (email: string, password: string, displayName?: string) => Promise<void>;
  logout: () => void;
  /** Validate the persisted token by calling `/api/auth/me`. Wipes the session on failure. */
  hydrate: () => Promise<void>;
  /** Refresh the locally-cached user (e.g. after enabling/disabling 2FA). */
  refreshUser: () => Promise<void>;
}

export const useAuthStore = create<AuthState>()(
  persist(
    (set, get) => ({
      token: null,
      user: null,
      hydrated: false,

      login: async (email, password, second) => {
        try {
          const { token, user } = await api.login({
            email,
            password,
            totp_code: second?.totp_code,
            recovery_code: second?.recovery_code,
          });
          set({ token, user, hydrated: true });
          return { status: 'ok' };
        } catch (err) {
          if (!(err instanceof ApiError)) return { status: 'network_error' };
          switch (err.code) {
            case 'totp_required':
              return { status: 'totp_required' };
            case 'totp_wrong':
              return { status: 'totp_wrong' };
            case 'invalid_credentials':
              return { status: 'invalid_credentials' };
            case 'rate_limited':
              return { status: 'rate_limited', retryAfterSeconds: err.retryAfterSeconds };
            default:
              return { status: 'network_error' };
          }
        }
      },

      signup: async (email, password, displayName) => {
        const { token, user } = await api.signup(email, password, displayName);
        set({ token, user, hydrated: true });
      },

      logout: () => {
        set({ token: null, user: null, hydrated: true });
      },

      hydrate: async () => {
        const token = get().token;
        if (!token) {
          set({ hydrated: true });
          return;
        }
        try {
          const { user } = await api.me(token);
          set({ user, hydrated: true });
        } catch (err) {
          if (err instanceof ApiError && err.status === 401) {
            set({ token: null, user: null, hydrated: true });
          } else {
            // Network blip — keep token, mark hydrated so UI proceeds with stale user info.
            set({ hydrated: true });
          }
        }
      },

      refreshUser: async () => {
        const token = get().token;
        if (!token) return;
        try {
          const { user } = await api.me(token);
          set({ user });
        } catch {
          // Best-effort refresh; if the server rejects, the next protected call will catch it.
        }
      },
    }),
    {
      name: 'linkora-auth',
      // Only persist token and user; transient flags stay in memory.
      partialize: (s) => ({ token: s.token, user: s.user }),
    },
  ),
);
